import { shouldPushToken } from '../../utils/adaToken';

const ADA_READY_EVENT = 'ada:ready';

// Ada is injected by GTM, possibly late (e.g. once the cookie consent is given), so the bridge
// keeps looking for it for about 10 minutes: every second during the first 30 seconds (normal
// page load), then every 10 seconds. That is at most 87 cheap property reads per page.
const DEFAULT_POLL_PHASES = [
    { intervalMs: 1000, attempts: 30 },
    { intervalMs: 10000, attempts: 57 }
];

const defaultTimers = {
    setInterval: (callback, delay) => setInterval(callback, delay),
    clearInterval: (id) => clearInterval(id)
};

/**
 * Sends the member access token to the Ada chat widget.
 *
 * Ada is loaded by GTM, so it may start before or after this bundle, and GTM may replace
 * window.adaSettings. The bridge therefore does not rely on Ada settings callbacks:
 * - it still writes the token in window.adaSettings.sensitiveMetaFields (read by Ada if it starts later);
 * - as soon as window.adaEmbed exists, it sends the token with setSensitiveMetaFields() and again
 *   on the 'ada:ready' event (checked in production: this updates the variable used by Ada actions,
 *   whether the chat is open or closed, even in an ongoing conversation);
 * - it sends every new token the Redux store receives.
 */
export const startAdaTokenBridge = ({
    store,
    win = window,
    now = () => Date.now(),
    timers = defaultTimers,
    pollPhases = DEFAULT_POLL_PHASES
}) => {
    let pushedToken = null;
    let attachedEmbed = null;

    // Ada is disabled on some pages (messaging v2) with `window.adaSettings = null`.
    const isAdaDisabled = () => win.adaSettings === null;

    const getEmbed = () => {
        const embed = win.adaEmbed;
        return embed && typeof embed.setSensitiveMetaFields === 'function' ? embed : null;
    };

    const getStoreAuth = () => store.getState().auth || {};

    const sendToEmbed = (token) => {
        const embed = getEmbed();
        if (!embed) {
            return;
        }
        Promise.resolve()
            .then(() => embed.setSensitiveMetaFields({ access_token: token }))
            .catch((e) => {
                console.warn('[Ada] setSensitiveMetaFields failed', e);
                if (pushedToken === token) {
                    pushedToken = null;
                }
            });
    };

    const push = (token) => {
        pushedToken = token;
        const settings = win.adaSettings || {};
        win.adaSettings = {
            ...settings,
            sensitiveMetaFields: { ...settings.sensitiveMetaFields, access_token: token }
        };
        sendToEmbed(token);
    };

    const sync = ({ force = false } = {}) => {
        if (isAdaDisabled()) {
            return;
        }
        const auth = getStoreAuth();
        if (
            shouldPushToken({
                token: auth.accessToken,
                expiresAt: auth.expiresAt,
                pushedToken,
                force,
                nowMs: now()
            })
        ) {
            push(auth.accessToken);
        }
    };

    const attachEmbed = (embed) => {
        attachedEmbed = embed;
        try {
            if (typeof embed.subscribeEvent === 'function') {
                Promise.resolve(embed.subscribeEvent(ADA_READY_EVENT, () => sync({ force: true }))).catch(
                    (e) => console.warn('[Ada] subscribeEvent failed', e)
                );
            }
        } catch (e) {
            console.warn('[Ada] subscribeEvent failed', e);
        }
        // Ada may have started without the token (it was loaded before this bundle): send it now.
        sync({ force: true });
    };

    // Runs the poll phases one after the other until Ada is found, the page opts out, or all
    // phases are exhausted.
    const pollForEmbed = (phaseIndex = 0) => {
        const phase = pollPhases[phaseIndex];
        if (!phase) {
            return;
        }
        let attempts = 0;
        const pollId = timers.setInterval(() => {
            attempts += 1;
            const found = getEmbed();
            if (found || isAdaDisabled()) {
                timers.clearInterval(pollId);
                if (found && !isAdaDisabled() && found !== attachedEmbed) {
                    attachEmbed(found);
                }
                return;
            }
            if (attempts >= phase.attempts) {
                timers.clearInterval(pollId);
                pollForEmbed(phaseIndex + 1);
            }
        }, phase.intervalMs);
    };

    if (isAdaDisabled()) {
        return;
    }

    store.subscribe(() => sync());
    const embed = getEmbed();
    if (embed) {
        // attachEmbed() runs the first sync itself, so the token is sent once.
        attachEmbed(embed);
    } else {
        sync();
        pollForEmbed();
    }
};
